import { getCartListItemHelperText } from '@cart-services/core/cartService'
import { type SpecificAddonCartItem } from '@cart-services/types'
import { isPreOrderActive } from '@cart-services/utils/findPreOrderProduct'
import { type PreOrderInfo } from '@hooks-api/types/dataModel/products'
import { Alert } from '@pets-ui/Alert/Alert'
import { Chip } from '@pets-ui/Chip/Chip'
import { Fade } from '@pets-ui/Fade/Fade'
import { HStack } from '@pets-ui/HStack/HStack'
import { Link } from '@pets-ui/Link/Link'
import { NumberInput } from '@pets-ui/NumberInput/NumberInput'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsiveCustom } from '@pets-ui/useResponsive/useResponsive'
import { VStack, type VStackProps } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { type MouseEventHandler, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AddonDiscountPrice } from '../AddonDiscountPrice/AddonDiscountPrice'
import { CartSalePrice } from '../CartSalePrice/CartSalePrice'
import { CouponConditionList, type CouponConditionListProps } from '../CouponConditionList/CouponConditionList'
import cartUiI18nInstance from '../i18n/i18nConfig'
import { type WpCartMobileMediaQuery } from '../WpCartLayout/useWpCartResponsive'
import { CartItemImage } from './CartItemImage'
import { CartListItemLayout } from './CartListItemLayout'
import { IconButtonDeleteItem } from './IconButtonDeleteItem'
import { SelectProductVariant } from './SelectProductVariant'
import { SoldOutText } from './SoldOutText'

const MOBILE: WpCartMobileMediaQuery = '@media (max-width: 983px)'

const styles = stylex.create({
  numberInputButton: {
    height: '24px',
    width: '24px',
  },
  numberInputWidth: {
    width: '26px',
  },
  numberInputError: {
    borderColor: colors.red600,
    borderStyle: 'solid',
    borderWidth: '1px',
  },
  couponConditionList: {
    zIndex: 1,
  },
})

export interface CartListItemProps {
  rootRef?: VStackProps['rootRef']
  quantity?: number
  maxQuantity?: number
  salePrice?: number
  attributes?: string
  originalPrice?: number
  /** 前端可用庫存，會分配在一般商品和加價購商品 */
  stock?: number
  productImgSrc?: string
  productImgWidth?: number
  productTitle?: ReactNode
  currency?: string
  isAddonV2?: boolean
  isAdjustedToMaxQuantity?: boolean
  addonV2CriterionsNotMet?: boolean
  addonProductV2NotFound?: boolean
  addonV2PurchaseLimit?: number
  couponCriterions?: CouponConditionListProps['couponCriterions']
  onQuantityChange?: ((value?: number) => void)
  onDelete?: MouseEventHandler<HTMLButtonElement>
  productPageUrl?: string
  onSelectVariantClick?: () => void
  errorHighlight?: boolean
  showToPromotionButton?: boolean
  addonV2SalePrice?: number
  productLifeLimit?: number
  selectVariantRootStyle?: stylex.StyleXStyles
  /** 母商品加價購限制數量 */
  parentProductPurchasedLimit?: number | null
  /** 是否超過母商品購買限制 */
  isParentLimitExceeded?: boolean
  /**
   * 主商品下的加價購商品
   */
  specificAddons?: SpecificAddonCartItem[]
  showUnitPrice?: boolean
  /** 預購資訊，傳入且為預購時於卡片底部顯示提示，目前僅業務專站（STM／merchant）使用 */
  preOrderInfo?: PreOrderInfo
}

export const CartListItem = (props: CartListItemProps) => {
  const {
    rootRef,
    maxQuantity,
    salePrice,
    quantity = 0,
    stock = 0,
    productImgSrc,
    productImgWidth,
    productTitle,
    currency,
    couponCriterions,
    onQuantityChange,
    onDelete,
    onSelectVariantClick,
    attributes,
    isAddonV2,
    isAdjustedToMaxQuantity,
    addonV2CriterionsNotMet,
    addonProductV2NotFound,
    addonV2PurchaseLimit,
    productPageUrl,
    errorHighlight,
    showToPromotionButton,
    addonV2SalePrice,
    productLifeLimit,
    selectVariantRootStyle,
    parentProductPurchasedLimit,
    isParentLimitExceeded,
    originalPrice,
    showUnitPrice = false,
    preOrderInfo,
  } = props

  const isOutOfStock = stock === 0
  const minQuantity = (isOutOfStock || (quantity === 0)) ? 0 : 1

  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })

  const handleQuantityChange: CartListItemProps['onQuantityChange'] = (value) => {
    if (value === undefined) return
    onQuantityChange?.(value)
  }

  const helperText = getCartListItemHelperText(
    stock,
    quantity,
    isAdjustedToMaxQuantity ?? false,
    t,
    addonV2PurchaseLimit,
    productLifeLimit,
    parentProductPurchasedLimit,
    isParentLimitExceeded,
  )
  const isMobile = useResponsiveCustom(MOBILE)
  const hasInputError = isParentLimitExceeded

  return (
    <VStack rootRef={rootRef} p={3} gap={3} backgroundColor={(errorHighlight || isOutOfStock) ? colors.red100 : colors.white} borderRadius={8}>
      <CartListItemLayout>
        <Link href={productPageUrl}>
          <CartItemImage
            productImgSrc={productImgSrc}
            productTitle={productTitle}
            productImgWidth={productImgWidth}
          />
        </Link>
        <VStack gap={2}>
          <Link href={productPageUrl} color={colors.grey900}>
            <HStack gap={2} alignItems="center" flexWrap="nowrap" maxWidth={isMobile ? undefined : 290}>
              {isAddonV2 && <Chip size="small" variant="square" color="red">{t('cartUi.addonOffer', { defaultValue: '加價購' })}</Chip>}
              <Text variant="subtitle" lineClamp={2}>
                {productTitle}
              </Text>
            </HStack>
          </Link>
          {attributes
          && (
            <VStack gap={1}>
              <SelectProductVariant error={isOutOfStock} onClick={isAddonV2 ? undefined : onSelectVariantClick} rootStyle={selectVariantRootStyle}>
                {attributes}
              </SelectProductVariant>
              {isOutOfStock && <Text color={colors.red600} variant="caption">{t('cartUi.changeOrRemoveProducts', { defaultValue: '請更換品項或移除' })}</Text>}
            </VStack>
          )}
          {addonV2CriterionsNotMet && <Text color={colors.red600} variant="caption">{t('cartUi.notMetAddonRequirement', { defaultValue: '未達加購門檻' })}</Text>}
        </VStack>
        {isMobile
          ? (
            <HStack gap={2} justifyContent="space-between" width="100%" alignItems="center">
              {addonProductV2NotFound
                ? <Text color={colors.red600} variant="caption">{t('cartUi.addonExpired', { defaultValue: '加價購已失效，請移除' })}</Text>
                : (
                  <>
                    <HStack gap={1} alignItems="flex-end">
                      {!isAddonV2 && <CartSalePrice currency={currency} salePrice={salePrice} quantity={quantity} priceType="totalPrice" /> }
                      {showUnitPrice && (
                        <CartSalePrice currency={currency} salePrice={salePrice} quantity={quantity} priceType="salePrice" />
                      )}
                    </HStack>
                    {isAddonV2 && (
                      <AddonDiscountPrice
                        currency={currency}
                        salePrice={addonV2SalePrice}
                        quantity={quantity}
                        originalPrice={originalPrice ?? salePrice}
                        variant="horizontal"
                        minWidth={50}
                      />
                    )}
                    {isOutOfStock
                      ? <SoldOutText>{t('cartUi.soldout', { defaultValue: '已售完' })}</SoldOutText>
                      : (
                        <VStack alignItems="center" mr={2}>
                          <NumberInput
                            color="white"
                            value={quantity}
                            min={minQuantity}
                            max={Math.min(maxQuantity ?? stock, stock, addonV2PurchaseLimit ?? stock)}
                            onChange={handleQuantityChange}
                            autoSelect
                            success={isAdjustedToMaxQuantity}
                            ButtonDecrementProps={{
                              'data-testid': 'button-decrease-quantity',
                              'rootStyle': styles.numberInputButton,
                            }}
                            ButtonIncrementProps={{
                              'data-testid': 'button-increase-quantity',
                              'rootStyle': styles.numberInputButton,
                            }}
                            rootStyle={styles.numberInputWidth}
                            error={hasInputError}
                          />
                          {helperText && <Text variant="caption" color={isParentLimitExceeded ? colors.red600 : colors.green400}>{helperText}</Text>}
                        </VStack>
                      )}
                  </>
                )}
            </HStack>
          )
          : (
            <HStack gap={2} justifyContent="space-between" width="100%" alignItems="center" minWidth={240}>
              {addonProductV2NotFound
                ? <Text color={colors.red600} variant="caption">{t('cartUi.addonExpired', { defaultValue: '加價購已失效，請移除' })}</Text>
                : (
                  <>
                    {showUnitPrice && <CartSalePrice currency={currency} salePrice={salePrice} quantity={quantity} priceType="salePrice" />}
                    {isOutOfStock
                      ? <SoldOutText>{t('cartUi.soldout', { defaultValue: '已售完' })}</SoldOutText>
                      : (
                        <VStack alignItems="center" gap={1}>
                          <NumberInput
                            color="white"
                            value={quantity}
                            min={minQuantity}
                            max={Math.min(maxQuantity ?? stock, stock, addonV2PurchaseLimit ?? stock)}
                            onChange={handleQuantityChange}
                            autoSelect
                            success={isAdjustedToMaxQuantity}
                            ButtonDecrementProps={{
                              'data-testid': 'button-decrease-quantity',
                              'rootStyle': styles.numberInputButton,
                            }}
                            ButtonIncrementProps={{
                              'data-testid': 'button-increase-quantity',
                              'rootStyle': styles.numberInputButton,
                            }}
                            rootStyle={styles.numberInputWidth}
                            error={hasInputError}
                          />
                          {helperText && <Text variant="caption" color={hasInputError ? colors.red600 : colors.green400}>{helperText}</Text>}
                        </VStack>
                      )}
                    <HStack minWidth={50} justifyContent="flex-end">
                      {!isAddonV2 && <CartSalePrice currency={currency} salePrice={salePrice} quantity={quantity} priceType="totalPrice" /> }
                      {isAddonV2 && (
                        <AddonDiscountPrice
                          currency={currency}
                          salePrice={addonV2SalePrice}
                          quantity={quantity}
                          originalPrice={originalPrice ?? salePrice}
                          variant="vertical"
                          minWidth={50}
                        />
                      )}
                    </HStack>
                  </>
                )}
            </HStack>
          )}
        <IconButtonDeleteItem onClick={onDelete} />
      </CartListItemLayout>
      {isAddonV2
        ? null
        : (
          <Fade in={couponCriterions && couponCriterions.length > 0} duration={300} rootStyle={styles.couponConditionList}>
            <VStack gap={1}>
              <CouponConditionList couponCriterions={couponCriterions} currency={currency} showToPromotionButton={showToPromotionButton} />
            </VStack>
          </Fade>
        )}
      {isPreOrderActive(preOrderInfo) && preOrderInfo?.description && (
        <Alert type="warning">{preOrderInfo.description}</Alert>
      )}
    </VStack>
  )
}
